import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import DatePicker from "components/inputs/DatePicker/DatePicker";
import TextInput from "components/inputs/TextInput/TextInput";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";
import { useDeleteMetadocMetadataMutation } from "services/metadoc-api";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

import type { ChangeEvent, FocusEvent } from "react";
import type {
  EditableMedicationValue,
  MedicationField,
} from "../../utils/codeMetadataUtils";

type MedicationCardProps = {
  medication: EditableMedicationValue;
  onFieldChange: (
    metadataId: number,
    field: MedicationField,
    value: string,
  ) => void;
  onFieldSave: (
    metadataId: number,
    field: MedicationField,
    value: string,
  ) => Promise<void>;
};

/**
 * normalizes text and textarea change payloads into a string value.
 */
const getInputValue = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
): string => (typeof event === "string" ? event : event.target.value);

/*
renders a single medication card with structured editing controls
*/
const MedicationCard = ({
  medication,
  onFieldChange,
  onFieldSave,
}: MedicationCardProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [deleteMetadocMetadata, { isLoading: isDeletingMedication }] =
    useDeleteMetadocMetadataMutation();

  const handleInputBlur =
    (field: MedicationField) => async (event: FocusEvent<HTMLInputElement>) => {
      await onFieldSave(medication.id, field, event.target.value);
    };

  const handleDeleteMedication = async () => {
    try {
      await deleteMetadocMetadata({ metadataId: medication.id }).unwrap();
      setIsDeleteModalOpen(false);
      setIsDeleted(true);
    } catch (error) {
      console.error("Failed to delete medication metadata:", error);
      KaboodleToaster.error("Failed to delete medication. Please try again.");
    }
  };

  if (isDeleted) {
    return null;
  }

  return (
    <>
      <Card
        heading={null}
        type="inverse"
        classNames="metadoc-viewer-workspace__medication-card"
      >
        <div className="metadoc-viewer-workspace__medication-card-content">
          <div className="metadoc-viewer-workspace__medication-main">
            <div className="metadoc-viewer-workspace__medication-grid">
              <TextInput
                label="Medication"
                name={`medication-${medication.id}-name`}
                value={medication.name}
                classNames="metadoc-viewer-workspace__medication-input"
                onChange={(event) => {
                  onFieldChange(medication.id, "name", getInputValue(event));
                }}
                onBlur={handleInputBlur("name")}
              />
              <TextInput
                label="Dosage"
                name={`medication-${medication.id}-dosage`}
                value={medication.dosage}
                classNames="metadoc-viewer-workspace__medication-input"
                onChange={(event) => {
                  onFieldChange(medication.id, "dosage", getInputValue(event));
                }}
                onBlur={handleInputBlur("dosage")}
              />
              <TextInput
                label="Frequency"
                name={`medication-${medication.id}-frequency`}
                value={medication.frequency}
                classNames="metadoc-viewer-workspace__medication-input"
                onChange={(event) => {
                  onFieldChange(
                    medication.id,
                    "frequency",
                    getInputValue(event),
                  );
                }}
                onBlur={handleInputBlur("frequency")}
              />
            </div>
            <div className="metadoc-viewer-workspace__medication-meta-grid">
              <DatePicker
                label="Date"
                name={`medication-${medication.id}-date`}
                className="metadoc-viewer-workspace__medication-date"
                initialDate={STRING_TO_DATE(medication.date)}
                isClearable={false}
                onDateChange={(date) => {
                  onFieldChange(
                    medication.id,
                    "date",
                    DATE_TO_STRING(date, "MM/dd/yyyy") ?? "",
                  );
                }}
                onBlur={() => {
                  void onFieldSave(medication.id, "date", medication.date);
                }}
              />
              <TextInput
                label="Provider"
                name={`medication-${medication.id}-provider`}
                value={medication.providerName}
                classNames="metadoc-viewer-workspace__medication-input"
                onChange={(event) => {
                  onFieldChange(
                    medication.id,
                    "providerName",
                    getInputValue(event),
                  );
                }}
                onBlur={handleInputBlur("providerName")}
              />
            </div>
          </div>
          <button
            type="button"
            className="metadoc-viewer-workspace__code-edit-trigger"
            onClick={() => {
              setIsDeleteModalOpen(true);
            }}
            disabled={isDeletingMedication}
          >
            Delete
          </button>
        </div>
      </Card>
      <KaboodleModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeletingMedication) {
            setIsDeleteModalOpen(false);
          }
        }}
        footer={
          <div className="flex jc-c">
            <Button
              className="delete"
              onClick={handleDeleteMedication}
              disabled={isDeletingMedication}
            >
              {isDeletingMedication ? "Deleting..." : "Confirm Delete"}
            </Button>
            <Button
              onClick={() => {
                setIsDeleteModalOpen(false);
              }}
              disabled={isDeletingMedication}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <Card heading="Delete Medication">
          <p>Are you sure you want to delete this medication?</p>
          <p>This action cannot be undone.</p>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default MedicationCard;
